import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Handshake, Loader2, ScanLine, TicketPercent } from "lucide-react";
import Modal from "@/components/Modal";
import AdminEmptyState from "@/components/AdminEmptyState";
import StudentCouponCard, { type StudentCoupon } from "@/components/student/StudentCouponCard";
import StudentQRScanner from "@/components/student/StudentQRScanner";
import StudentSponsorCard from "@/components/student/StudentSponsorCard";
import StudentSponsorRedeemModal from "@/components/student/StudentSponsorRedeemModal";
import { GetCouponAPI, COUPON_PAGE_SIZE } from "@/services/api/coupon/getCoupons";
import { RedeemCouponAPI } from "@/services/api/coupon/redeemCoupon";
import { GetSponsorsAPI, type SponsorCategoryCount, type SponsorItem } from "@/services/api/sponsor/getSponsors";
import { GetSponsorQuantityCapAPI } from "@/services/api/sponsor/quantityCap";
import { GetRedeemedSponsorsAPI } from "@/services/api/sponsor/getRedeemedSponsors";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { getRedeemMessageKey } from "@/utils/couponFormat";
import { isTruthyFlag } from "@/utils/businessCoupon";

const SPONSOR_PAGE_SIZE = 20;
const SPONSOR_MAP_PAGE_SIZE = 200;
/** The API reports "there is nothing to show" with this status, not an array. */
const NO_COUPONS_STATUS = -30038;

type Tab = "coupon" | "sponsor";

const TABS: { key: Tab; label: string; icon: typeof TicketPercent }[] = [
  { key: "sponsor", label: "Sponsor", icon: Handshake },
  { key: "coupon", label: "Coupon", icon: TicketPercent },
];

/** Card-shaped pulse placeholder for the coupon / sponsor card grids. */
const CardGridSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
      >
        <div className="flex items-center gap-3">
          <div className="h-16 w-16 shrink-0 rounded-xl bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-3/5 rounded-lg bg-gray-100" />
            <div className="h-3 w-2/5 rounded-md bg-gray-100" />
            <div className="h-3 w-4/5 rounded-md bg-gray-100" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="h-3 w-1/3 rounded-md bg-gray-100" />
          <div className="h-8 w-20 rounded-xl bg-gray-100" />
        </div>
      </div>
    ))}
  </>
);

/**
 * Redeem centre (port of the RN `CouponScreen`): the available coupon grid with
 * a `Redeem` action plus the shared QR scanner for redeeming a coupon at the
 * counter. The `coupons` table stores both coupons and sponsor offers, so both
 * tabs read the same paged feed and are split client-side by `offer_type`.
 * Everything the student already redeemed lives on `/student/redeemed`.
 */
const StudentCouponsPage: React.FC = () => {
  const { t } = useTranslation();

  const [tab, setTab] = useState<Tab>("sponsor");
  const [offers, setOffers] = useState<StudentCoupon[]>([]);
  const [couponLoading, setCouponLoading] = useState(true);
  const [couponError, setCouponError] = useState(false);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);

  const [sponsors, setSponsors] = useState<SponsorItem[]>([]);
  const [sponsorLoading, setSponsorLoading] = useState(true);
  const [redeemedSponsorTypes, setRedeemedSponsorTypes] = useState<
    Record<string, "personal" | "society">
  >({});
  const [redeemingSponsor, setRedeemingSponsor] = useState<SponsorItem | null>(null);
  const [maxParticipants, setMaxParticipants] = useState(500);
  /**
   * Per-sponsor participants / quantity reported by the cards, so the redeem
   * modal opens with whatever the student already set on the card.
   */
  const [sponsorParticipants, setSponsorParticipants] = useState<
    Record<string, number>
  >({});
  const [sponsorQuantity, setSponsorQuantity] = useState<Record<string, number>>(
    {},
  );
  const [sponsorCategory, setSponsorCategory] = useState<string | null>(null);
  /** Server-side counts for the whole catalog, so the chips never drift. */
  const [sponsorCategoryCounts, setSponsorCategoryCounts] = useState<
    SponsorCategoryCount[]
  >([]);
  /** The category the rows currently on screen belong to. */
  const [sponsorsCategory, setSponsorsCategory] = useState<string | null>(null);

  const [alertText, setAlertText] = useState("");
  const [scannerVisible, setScannerVisible] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);

  /**
   * The paging state lives in refs, not in state: the scroll handler reads it
   * when it runs, so it always sees the page that was asked for last. A state
   * value would be one render behind and the list would ask for a page it has
   * already loaded.
   */
  /** The next page to ask for. */
  const couponPageRef = useRef(1);
  /** Whether the feed may still have another page. */
  const couponHasMoreRef = useRef(false);
  /** Whether a page request is running. */
  const couponLoadingRef = useRef(false);
  /** The id of the newest coupon request: an older response is stale. */
  const couponRequestRef = useRef(0);

  const sponsorPageRef = useRef(1);
  const sponsorHasMoreRef = useRef(false);
  const sponsorLoadingRef = useRef(false);
  const sponsorRequestRef = useRef(0);

  /**
   * Fetch one page of sponsors for the CURRENT category filter. The server does
   * the filtering and returns the counts for every category, so the client
   * never has to walk the rows it happens to have loaded.
   */
  const fetchSponsors = useCallback(
    async (targetPage: number, mode: "initial" | "more") => {
      if (sponsorLoadingRef.current) return;
      sponsorLoadingRef.current = true;
      const requestId = ++sponsorRequestRef.current;
      setSponsorLoading(true);
      try {
        const resp = await GetSponsorsAPI({
          page: targetPage,
          pageSize: SPONSOR_PAGE_SIZE,
          category: sponsorCategory,
        });
        if (requestId !== sponsorRequestRef.current) {
          // A newer request took over: these rows belong to a list that is
          // gone, so they must not land.
          return;
        }
        const result = resp?.result;
        if (resp?.status !== 0 || !Array.isArray(result?.sponsors)) {
          // Leave the chips alone on a failed page load; only a failed first
          // page clears the list.
          if (mode === "initial") {
            setSponsors([]);
            setSponsorsCategory(sponsorCategory);
            sponsorHasMoreRef.current = false;
          } else {
            sponsorHasMoreRef.current = false;
          }
          return;
        }
        const rows = result.sponsors;
        sponsorPageRef.current = targetPage + 1;
        // `total` counts the current filter, so this is exact rather than a
        // guess from a short page.
        sponsorHasMoreRef.current =
          rows.length > 0 && targetPage * SPONSOR_PAGE_SIZE < result.total;
        setSponsorsCategory(sponsorCategory);
        setSponsorCategoryCounts(result.categoryCounts);
        setSponsors((prev) => {
          if (mode === "initial") return rows;
          const existing = new Set(prev.map((item) => item.id));
          return [...prev, ...rows.filter((item) => !existing.has(item.id))];
        });
      } finally {
        if (requestId === sponsorRequestRef.current) {
          sponsorLoadingRef.current = false;
          setSponsorLoading(false);
        }
      }
    },
    [sponsorCategory],
  );


  useEffect(() => {
    // A category the server no longer lists can't be shown, so drop it.
    if (
      sponsorCategory &&
      !sponsorCategoryCounts.some((entry) => entry.category === sponsorCategory)
    ) {
      setSponsorCategory(null);
    }
  }, [sponsorCategoryCounts, sponsorCategory]);

  /** True while the rows on screen belong to the selected category. */
  const sponsorsMatchCategory = sponsorsCategory === sponsorCategory;

  const fetchQuantityCap = useCallback(async () => {
    const resp = await GetSponsorQuantityCapAPI();
    if (!resp.result) return;
    setMaxParticipants(resp.result.maxParticipants);
  }, []);

  const fetchRedeemedTypes = useCallback(async () => {
    const resp = await GetRedeemedSponsorsAPI(1, SPONSOR_MAP_PAGE_SIZE);
    if (resp?.status !== 0) return;
    const map: Record<string, "personal" | "society"> = {};
    (resp.result?.sponsors ?? []).forEach((item) => {
      const type = item.activity?.type;
      if (item.id && (type === "personal" || type === "society")) {
        map[item.id] = type;
      }
    });
    setRedeemedSponsorTypes(map);
  }, []);

  /**
   * Load one page of the coupon feed.
   *
   * `mode` decides what happens with the rows: "initial" replaces the list
   * (the first page, or a refresh), "more" appends them. Only one request
   * runs at a time — a second one would ask for the same page and append its
   * rows twice — and a response that a newer request has superseded is
   * dropped, so a slow page can never overwrite a fresh list.
   */
  const fetchCoupons = useCallback(
    async (targetPage: number, mode: "initial" | "more") => {
      if (couponLoadingRef.current) return;
      couponLoadingRef.current = true;
      const requestId = ++couponRequestRef.current;
      setCouponLoading(true);
      try {
        const resp = await GetCouponAPI(targetPage);
        if (requestId !== couponRequestRef.current) {
          // A newer request took over: these rows belong to a list that is
          // gone, so they must not land.
          return;
        }
        if (resp?.status === NO_COUPONS_STATUS) {
          setCouponError(false);
          couponPageRef.current = targetPage;
          couponHasMoreRef.current = false;
          setOffers([]);
          return;
        }
        const result = resp?.result;
        if (resp?.status !== 0 || !Array.isArray(result)) {
          if (mode === "initial") {
            setCouponError(true);
            couponHasMoreRef.current = false;
          }
          return;
        }
        setCouponError(false);
        couponPageRef.current = targetPage + 1;
        // A short page is how the feed says it has ended.
        couponHasMoreRef.current = result.length >= COUPON_PAGE_SIZE;
        setOffers((prev) => {
          if (mode === "initial") return result;
          const existing = new Set(prev.map((item) => item.id));
          return [...prev, ...result.filter((item) => !existing.has(item.id))];
        });
      } finally {
        if (requestId === couponRequestRef.current) {
          couponLoadingRef.current = false;
          setCouponLoading(false);
        }
      }
    },
    [],
  );

  /**
   * Whether the end of the list is still on screen.
   *
   * The measurement waits for the next frame: the rows of the page that was
   * just appended are only in the DOM after React rendered them, so measuring
   * right away would report the layout of the list from before the append.
   */
  const isListEndOnScreen = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        requestAnimationFrame(() => {
          const sentinel = sentinelRef.current;
          if (!sentinel) {
            resolve(false);
            return;
          }
          resolve(
            sentinel.getBoundingClientRect().bottom <= window.innerHeight + 200,
          );
        });
      }),
    [],
  );

  /**
   * Load the next page of the coupon feed and keep going while the end of the
   * list is still on screen: a list that is shorter than the window cannot be
   * scrolled, so the scroll that would ask for the next page never comes.
   */
  const loadMoreCoupons = useCallback(async () => {
    if (couponLoadingRef.current) return;
    while (couponHasMoreRef.current && !couponLoadingRef.current) {
      const requestedPage = couponPageRef.current;
      await fetchCoupons(requestedPage, "more");
      // A failed request leaves the page counter where it was; retrying from
      // this loop would hammer the API, so the next scroll tries again.
      if (couponPageRef.current === requestedPage) return;
      if (!(await isListEndOnScreen())) return;
    }
  }, [fetchCoupons, isListEndOnScreen]);


  /**
   * Put the list back to the first page. A request that is still running is
   * superseded: its rows belong to the list this reload replaces.
   */
  const reloadCoupons = useCallback(async () => {
    couponRequestRef.current += 1;
    couponLoadingRef.current = false;
    couponPageRef.current = 1;
    couponHasMoreRef.current = false;
    await fetchCoupons(1, "initial");
    await loadMoreCoupons();
  }, [fetchCoupons, loadMoreCoupons]);

  /**
   * Load the next page of sponsors and keep going while the end of the list is
   * still on screen (see `loadMoreCoupons`).
   */
  const loadMoreSponsors = useCallback(async () => {
    if (sponsorLoadingRef.current) return;
    while (sponsorHasMoreRef.current && !sponsorLoadingRef.current) {
      const requestedPage = sponsorPageRef.current;
      await fetchSponsors(requestedPage, "more");
      // Same guard as `loadMoreCoupons`: no progress means a failed page.
      if (sponsorPageRef.current === requestedPage) return;
      if (!(await isListEndOnScreen())) return;
    }
  }, [fetchSponsors, isListEndOnScreen]);


  /**
   * Put the sponsor list back to the first page (see `reloadCoupons`).
   */
  const reloadSponsors = useCallback(async () => {
    sponsorRequestRef.current += 1;
    sponsorLoadingRef.current = false;
    sponsorPageRef.current = 1;
    sponsorHasMoreRef.current = false;
    await fetchSponsors(1, "initial");
    await loadMoreSponsors();
  }, [fetchSponsors, loadMoreSponsors]);

  useEffect(() => {
    // The first page, then the pages that fit on the screen.
    void (async () => {
      await fetchCoupons(1, "initial");
      await loadMoreCoupons();
    })();
  }, [fetchCoupons, loadMoreCoupons]);

  // `fetchSponsors` closes over the category filter, so picking a chip resets
  // the list to page 1 of the newly filtered feed. The request that is running
  // is superseded: its rows belong to the old filter.
  useEffect(() => {
    sponsorRequestRef.current += 1;
    sponsorLoadingRef.current = false;
    sponsorPageRef.current = 1;
    sponsorHasMoreRef.current = false;
    void (async () => {
      await fetchSponsors(1, "initial");
      await loadMoreSponsors();
    })();
  }, [fetchSponsors, loadMoreSponsors]);

  useEffect(() => {
    fetchRedeemedTypes();
    fetchQuantityCap();
  }, [fetchRedeemedTypes, fetchQuantityCap]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    /**
     * The list is scrolled by the document (the student layout is a normal
     * page flow, not an inner scroll area), so the sentinel at the bottom of
     * the page is what tells us that the end of the list is in view.
     */
    const maybeLoadMore = () => {
      if (tab === "coupon") {
        void loadMoreCoupons();
        return;
      }
      void loadMoreSponsors();
    };

    if (typeof IntersectionObserver === "undefined") {
      const onScroll = () => {
        const reachedBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 400;
        if (reachedBottom) maybeLoadMore();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [tab, loadMoreCoupons, loadMoreSponsors]);


  const couponOffers = useMemo(
    () => offers.filter((o) => (o.offer_type ?? "coupon") !== "sponsor"),
    [offers],
  );

  const refreshAll = async () => {
    await reloadCoupons();
    await reloadSponsors();
    await fetchRedeemedTypes();
  };

  const { refreshing, touchProps } = usePullToRefresh(refreshAll);

  const handleRedeem = async (couponId: string) => {
    if (redeemingId) return;
    // The card greys the action out, but a tap still explains why instead of
    // calling the API (which refuses suspended offers anyway).
    const target = offers.find((offer) => offer.id === couponId);
    if (isTruthyFlag(target?.is_business_inactive)) {
      setAlertText(
        t("This offer is suspended and cannot be redeemed right now."),
      );
      return;
    }
    setRedeemingId(couponId);
    const resp = await RedeemCouponAPI(couponId);
    setRedeemingId(null);
    if (resp?.status === 0) {
      setAlertText(t("Redeem The Coupon Successfully"));
      return;
    }
    setAlertText(t(getRedeemMessageKey(resp?.status)));
  };

  const handleSponsorRedeemed = async (status?: number) => {
    if (status === 0) {
      setAlertText(t("Redeem successful!"));
      await fetchRedeemedTypes();
      return;
    }
    setRedeemingSponsor(null);
  };

  const handleScanned = () => {
    void reloadCoupons();
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" {...touchProps}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("Coupons & Sponsors")}
        </h1>
        <button
          type="button"
          onClick={() => setScannerVisible(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#f59200]"
        >
          <ScanLine size={18} />
          {t("Scan QR to redeem")}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {TABS.map((item) => {
          const Icon = item.icon;
          const isActive = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                isActive
                  ? "bg-[#ffa217] text-white"
                  : "bg-white text-gray-600 ring-1 ring-gray-200 hover:text-[#ffa217]"
              }`}
            >
              <Icon size={14} />
              {t(item.label)}
            </button>
          );
        })}
      </div>

      {refreshing && (
        <div className="mt-3 flex justify-center">
          <Loader2 size={18} className="animate-spin text-[#ffa217]" />
        </div>
      )}

      {tab === "coupon" && (
        <div className="mt-4">
          {couponLoading && couponOffers.length === 0 ? (
            <CardGridSkeleton count={4} />
          ) : couponError ? (
            <div className="rounded-2xl border border-gray-100 bg-white">
              <AdminEmptyState
                title={t("Something went wrong")}
                message={t("Please try again later.")}
                actionLabel={t("Retry")}
                onAction={() => void reloadCoupons()}
              />
            </div>
          ) : couponOffers.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white">
              <AdminEmptyState
                iconName="ticket-outline"
                title={t("No coupons")}
                message={t("Refresh to see the latest offers")}
                actionLabel={t("Refresh")}
                onAction={() => void reloadCoupons()}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {couponOffers.map((coupon) => (
                <StudentCouponCard
                  key={coupon.id}
                  coupon={coupon}
                  pending={redeemingId === coupon.id}
                  onRedeem={handleRedeem}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "sponsor" && (
        <div className="mt-4">
          {/* The chips stay mounted even when a filter has no rows, so the
              selection can always be cleared. */}
          {sponsorCategoryCounts.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSponsorCategory(null)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  sponsorCategory === null
                    ? "border-[#ffa217] bg-[#ffa217] text-white"
                    : "border-gray-200 bg-white text-gray-600"
                }`}
              >
                {t("All categories")}
              </button>
              {sponsorCategoryCounts.map(({ category, count }) => (
                <button
                  key={category}
                  type="button"
                  onClick={() =>
                    setSponsorCategory(sponsorCategory === category ? null : category)
                  }
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    sponsorCategory === category
                      ? "border-[#ffa217] bg-[#ffa217] text-white"
                      : "border-gray-200 bg-white text-gray-600"
                  }`}
                >
                  {category} ({count})
                </button>
              ))}
            </div>
          )}
          {sponsorLoading && (sponsors.length === 0 || !sponsorsMatchCategory) ? (
            <CardGridSkeleton count={4} />
          ) : sponsorCategory && sponsors.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-6 text-center text-sm text-gray-500">
              {t("No sponsors in this category")}
            </div>
          ) : sponsors.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white">
              <AdminEmptyState
                iconName="inbox-outline"
                title={t("No sponsors")}
                message={t("Refresh to see the latest sponsors")}
                actionLabel={t("Refresh")}
                onAction={() => void reloadSponsors()}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sponsors.map((sponsor) => (
                <StudentSponsorCard
                  key={sponsor.id}
                  sponsor={sponsor}
                  redeemedType={redeemedSponsorTypes[sponsor.id] ?? null}
                  onRedeemClick={setRedeemingSponsor}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={sentinelRef} />

      <Modal visible={scannerVisible} onClose={() => setScannerVisible(false)}>
        <StudentQRScanner
          active={scannerVisible}
          onClose={() => setScannerVisible(false)}
          onScanned={handleScanned}
        />
      </Modal>

      <Modal visible={Boolean(alertText)} onClose={() => setAlertText("")}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{alertText}</p>
          <button
            type="button"
            onClick={() => setAlertText("")}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      {redeemingSponsor && (
        <StudentSponsorRedeemModal
          key={redeemingSponsor.id}
          visible
          sponsor={redeemingSponsor}
          maxParticipants={maxParticipants}
          cardParticipants={sponsorParticipants[redeemingSponsor.id] ?? null}
          quantity={sponsorQuantity[redeemingSponsor.id] ?? null}
          onQuantityChange={(quantity) =>
            setSponsorQuantity((prev) => ({
              ...prev,
              [redeemingSponsor.id]: quantity,
            }))
          }
          onParticipantsChange={(participants) =>
            setSponsorParticipants((prev) => ({
              ...prev,
              [redeemingSponsor.id]: participants,
            }))
          }
          onClose={() => setRedeemingSponsor(null)}
          onRedeemed={handleSponsorRedeemed}
        />
      )}
    </div>
  );
};

export default StudentCouponsPage;
